// Coordinated native Google transport; consent preferences remain authoritative.
const CAMPAIGN = 'ColorType_191026_EU_DSP_[Winners]_hairstyle';
const sent = new Set();
const resolvedCaptures = new WeakMap();
const ROUTE_PARAM = 'ca_ads_route';
const ROUTE_VALUE = 'stape_eu_v1';
const EUROPE_ROUTE = 'stape_google_europe_v2';
const COUNTRIES = 'AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE IS LI NO GB CH'.split(' ');
const validScope = c => c?.version === 2 && c.eligible === true && ['google','Google'].includes(c.source) && COUNTRIES.includes(c.country);

// Route continuity only: no visitor/order identifier, cookie or consent grant.
export function decorateStapeRedirect(value) {
  try {
    if (typeof window === 'undefined') return value;
    const params = new URLSearchParams(window.location.search);
    const campaigns = params.getAll('utm_campaign');
    const markers = params.getAll(ROUTE_PARAM);
    const route = window.caStapePurchaseQueued === EUROPE_ROUTE ? EUROPE_ROUTE :
      window.caStapePurchaseQueued ? ROUTE_VALUE :
      markers.length === 1 && [ROUTE_VALUE, EUROPE_ROUTE].includes(markers[0]) ? markers[0] : null;
    if (!route) return value;
    if (route === ROUTE_VALUE && campaigns.length && !(campaigns.length === 1 && campaigns[0] === CAMPAIGN)) return value;
    const routeObject = value && typeof value === 'object' && typeof value.pathname === 'string';
    const target = new URL(routeObject ? value.pathname + (value.search || '') + (value.hash || '') : value, window.location.href);
    if (target.protocol !== 'https:' || !['payment.color-analysis.pro', 'quiz2.color-analysis.pro',
      'staging-payment.color-analysis.pro', 'staging-quiz.color-analysis.pro'].includes(target.hostname)) return value;
    target.searchParams.set(ROUTE_PARAM, route);
    if (routeObject) return {...value, pathname: target.pathname, search: target.search, hash: target.hash};
    return value.startsWith('/') && !value.startsWith('//') ? target.pathname + target.search + target.hash : target.href;
  } catch (_) { return value; }
}

export const getStapeCampaignFromSearch = search => {
  const campaigns = new URLSearchParams(search).getAll('utm_campaign');
  return campaigns.length === 1 && campaigns[0] === CAMPAIGN ? CAMPAIGN : null;
};

// Ready context stays synchronous so purchase enqueue/redirect markers precede
// navigation. Unresolved scope is background-only and never gates payment.
export function captureStapeAttribution(search) {
  if (typeof window === 'undefined') return null;
  const consent = window.caConsent;
  if (consent?.stapeScope?.version === 2 && !consent.ready) {
    const pending = Promise.resolve(consent.scopeReady).then(() =>
      window.caConsent === consent ? getStapeAttributionFromSearch(search) : null
    ).catch(() => null).then(context => {
      resolvedCaptures.set(pending, context);
      return context;
    });
    return pending;
  }
  return getStapeAttributionFromSearch(search);
}

// A capture that settled during Stripe processing must not add a microtask
// before enqueueing the purchase and decorating an immediate redirect.
export function resolveCapturedStapeAttribution(capture) {
  return resolvedCaptures.has(capture) ? resolvedCaptures.get(capture) : capture;
}

// Capture at payment initiation; later URL changes cannot reattribute a charge.
export function getStapeAttributionFromSearch(search) {
  if (typeof window === 'undefined') return null;
  const scope = window.caConsent?.stapeScope;
  if (scope?.version !== 2) {
    const campaign = getStapeCampaignFromSearch(search);
    return campaign ? Object.freeze({version: 1, campaign}) : null;
  }
  const p = new URLSearchParams(search), sources = p.getAll('utm_source'), campaigns = p.getAll('utm_campaign');
  if (!validScope(scope) || !window.caConsent.ready || sources.length > 1 ||
      (sources.length === 1 && sources[0] !== scope.source)) return null;
  return Object.freeze({...scope, campaign: campaigns.length === 1 && campaigns[0].length <= 256 ? campaigns[0] : ''});
}

export function emitStapePurchase({ type, transactionId, paymentStatus, value, currency, acquisitionCampaign, acquisitionContext } = {}, onComplete) {
  try {
    if (process.env.REACT_APP_STAPE_EU_PURCHASE_ENABLED !== 'true' || typeof window === 'undefined') return false;
    const consent = window.caConsent;
    if (!consent?.ready || !consent.active || !consent.googleAdsAdvanced || !consent.allows('googleAds')) return false;
    const europe = consent.stapeScope?.version === 2;
    const campaign = acquisitionContext?.campaign ?? acquisitionCampaign;
    if (europe ? !validScope(acquisitionContext) || typeof campaign !== 'string' || campaign.length > 256 : campaign !== CAMPAIGN) return false;
    if (type !== 'trial' || paymentStatus !== 'succeeded' ||
        typeof transactionId !== 'string' || !/^pi_[A-Za-z0-9]{1,96}$/.test(transactionId) ||
        !Number.isFinite(value) || value <= 0 || typeof currency !== 'string' || !/^[a-zA-Z]{3}$/.test(currency)) return false;
    if (sent.has(transactionId)) return false;
    // No storage identifiers or consent overrides. The Google tag must carry the
    // actual consent signals: https://developers.google.com/tag-platform/tag-manager/server-side/consent-mode
    const dataLayer = (window.dataLayer = window.dataLayer || []);
    dataLayer.push({
      event: 'ca_stape_purchase',
      ...(onComplete ? { eventCallback: onComplete, eventTimeout: 1500 } : {}),
      ca_stape_purchase: {
        transaction_id: transactionId,
        value,
        currency: currency.toUpperCase(),
        ca_acquisition_campaign: campaign,
        ...(europe ? {ca_acquisition_source: acquisitionContext.source,
          ca_acquisition_country: acquisitionContext.country, ca_scope_version: 'google_europe_v2'} : {}),
      },
    });
    sent.add(transactionId);
    window.caStapePurchaseQueued = europe ? EUROPE_ROUTE : true;
    return true;
  } catch (_) {
    // Tracking must never interrupt successful checkout or authorize a retry.
    return false;
  }
}

// Give the native transport time to finish before a full-document redirect.
// A blocked tag must not trap a paid customer on checkout.
export function sendStapePurchase(input) {
  return new Promise(resolve => {
    const timer = setTimeout(resolve, 1500);
    const complete = () => { clearTimeout(timer); resolve(); };
    if (!emitStapePurchase(input, complete)) complete();
  });
}
